import { ActionableNotification, InlineNotification } from '@carbon/react';
import { useAtomValue } from 'jotai';
import { OutputSection } from 'components/output-section/output-section';
import { activeFilterCountAtom } from './atoms';
import { useRollupOptions } from './hooks';
import { ValueRollupTableCard } from './value-rollup-table-card';
import { VisualSummaryChartCard } from './visual-summary-chart-card';
import styles from './value-rollup-section.module.scss';

/**
 * Output Aggregation section SHELL. Owns only what the two cards SHARE: the static
 * options catalog (`useRollupOptions` — one fetch, `staleTime: Infinity`; passed down so
 * neither card re-fetches it), the section heading + a spinner for that initial catalog
 * load, the "Filtered view" banner, and the options error (a catalog failure blocks both
 * cards — there are no pickers without it).
 *
 * Each card (`ValueRollupTableCard` / `VisualSummaryChartCard`) is INDEPENDENTLY
 * connected — it subscribes to its OWN view-state atoms and runs its OWN query — so a
 * table-only change re-renders only the table card and a chart-only change only the chart
 * card. The shared filters / spatial scope are read inside each card, so a left-panel
 * filter change re-renders (and refetches) BOTH. This shell only re-renders on the
 * (one-time) options load and on a filter-count change (the banner) — not on any card's
 * view-state churn. Per-card data errors render inside each card; the section card is a
 * PERMANENT fixture (package mode / a mid-run calc render the cards empty).
 *
 * @return {JSX.Element} The Output Aggregation section.
 */
export function ValueRollupSection() {
  const {
    data: options,
    error: optionsError,
    isFetching: isOptionsFetching,
    refetch: refetchOptions,
  } = useRollupOptions();
  const activeFilterCount = useAtomValue(activeFilterCountAtom);

  return (
    <OutputSection title="Output Aggregation" defaultExpanded>
      {activeFilterCount > 0 && (
        <InlineNotification
          className={styles.banner}
          kind="info"
          lowContrast
          hideCloseButton
          title="Filtered view"
          subtitle="Showing results based on the filters applied in the left panel."
        />
      )}

      {optionsError && !isOptionsFetching && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the aggregation"
          subtitle={optionsError.message}
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetchOptions()}
        />
      )}

      <ValueRollupTableCard
        options={options}
        optionsLoading={isOptionsFetching}
      />
      <VisualSummaryChartCard
        options={options}
        optionsLoading={isOptionsFetching}
      />
    </OutputSection>
  );
}
